import { useState } from "react";
import dayjs from "dayjs";
import { Dialog } from "@mui/material";
import { Asset } from "app/types";
import { btnDelay, useScale } from "common/utils";
import { ParentCloseBtn, FadeTransition } from "common/elements";
import i18n from "features/shared/i18n";
import { ParentConfirmModal } from "./ParentConfirmModal";

interface AssetPreviewModalProps {
  asset: Asset;
  onClose: () => void;
  onDelete?: (asset: Asset) => void;
}

function isAIAsset(asset: Asset): boolean {
  return asset.tags?.startsWith("ai_generated:") ?? false;
}

export const AssetPreviewModal = ({ asset, onClose, onDelete }: AssetPreviewModalProps) => {
  const { width, scale } = useScale();
  // 狭い画面（scale下限クランプ時）でも収まるよう、設計幅を上限に可変にする
  const modalWidth = Math.min(700, width / scale - 16);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleOpenDeleteConfirm = () => {
    btnDelay(() => setShowDeleteConfirm(true));
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    onDelete?.(asset);
    onClose();
  };

  const isAI = isAIAsset(asset);
  const isSvg = /.svg/.test(asset.url);

  // 素材タイプを判定
  const getAssetType = () => {
    if (isAI) return "ai_image";
    if (isSvg) return "sketchbook";
    return "album";
  };
  const assetType = getAssetType();

  return (
    <Dialog
      fullScreen
      open={true}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        <div style={{ transform: `scale(${scale})` }}>
          <div
            style={{ width: modalWidth }}
            className="flex-col-el justify-start relative border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30"
          >
            <div className="flex-col-view justify-start w-full h-full relative p-6 rounded-[8px] bg-white">
              <ParentCloseBtn close={onClose} />
              {/* 画像プレビュー */}
              <div className="flex-col-center w-full h-[480px] overflow-hidden bg-gray2-20 p-4 mb-4 mt-10">
                <img
                  src={asset.url}
                  alt={asset.name}
                  className="w-full h-full object-contain bg-white"
                />
              </div>

              {/* 素材情報 */}
              <div className="flex-col-el gap-3">
                <div className="flex-row-el items-center gap-3">
                  <h2 className="text-[30px] text-textcolor-black whitespace-pre-wrap break-words flex-1">
                    {asset.name || i18n.t("MSG_PARENT_PAGE_PLAYER_ASSET_UNNAMED")}
                  </h2>
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-60/20 text-blue-100 rounded-full text-[18px] shrink-0">
                    <span className="material-symbols-outlined text-[21px]">
                      {assetType === "sketchbook"
                        ? "draw"
                        : assetType === "ai_image"
                          ? "auto_awesome"
                          : "photo"}
                    </span>
                    {assetType === "sketchbook"
                      ? i18n.t("MSG_PARENT_PAGE_PLAYER_SKETCHBOOK")
                      : assetType === "ai_image"
                        ? i18n.t("MSG_PARENT_PAGE_PLAYER_AI_IMAGE")
                        : i18n.t("MSG_PARENT_PAGE_PLAYER_ALBUM")}
                  </span>
                </div>

                <div className="flex-row-el flex-wrap items-center gap-4 text-[19px] text-gray2-100 pt-2 border-t border-outline">
                  <span>
                    {i18n.t("MSG_PARENT_PAGE_PLAYER_ASSET_CREATED")}:{" "}
                    {dayjs(asset.created_at).format("YYYY-MM-DD")}
                  </span>
                  {asset.metadata?.width && asset.metadata?.height && (
                    <span>
                      {i18n.t("MSG_PARENT_PAGE_PLAYER_ASSET_SIZE")}:{" "}
                      {Math.floor(Number(asset.metadata.width))} ×{" "}
                      {Math.floor(Number(asset.metadata.height))}
                    </span>
                  )}
                  {asset.file_size && <span>{Math.floor(asset.file_size / 1024)}KB</span>}
                </div>

                {/* 削除ボタン */}
                <div className="flex-row-el justify-end mt-4 pt-4 border-t border-gray2-30">
                  <button
                    onClick={handleOpenDeleteConfirm}
                    className="bg-danger text-white rounded-[12px] py-2.5 px-5 text-[21px] border-none cursor-pointer hover:opacity-90 transition-all"
                  >
                    {i18n.t("MSG_PARENT_PAGE_PLAYER_ASSET_DELETE_BTN")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ParentConfirmModal
        open={showDeleteConfirm}
        icon="delete"
        title={i18n.t("MSG_PARENT_PAGE_PLAYER_ASSET_DELETE_TITLE")}
        message={i18n.t("MSG_PARENT_PAGE_PLAYER_ASSET_DELETE_MESSAGE", {
          name: asset.name || i18n.t("MSG_PARENT_PAGE_PLAYER_ASSET_UNNAMED"),
        })}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
      />
    </Dialog>
  );
};
